import { useCallback, useEffect, useState } from "react";
import { integrationService } from "@/services";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { TriangleAlert, Loader2, RotateCw } from "lucide-react";
import { useTenantScope, TenantPicker, SelectTenantNotice, PageHeader, Badge, ERROR_STYLE, fmtDate } from "./shared";

const STATUSES = ["pending", "resolved", "dead"];

export default function ErrorLogsPage() {
  const scope = useTenantScope();
  const { isSuper, tenantId, params } = scope;
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const blocked = isSuper && !tenantId;

  const load = useCallback(() => {
    if (blocked) { setItems([]); return; }
    setLoading(true);
    integrationService.errorLogs({ ...params, status, size: 100 })
      .then((d) => setItems(d.items || []))
      .catch((e) => toast.error(apiErrorMessage(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [blocked, params, status]);
  useEffect(() => { load(); }, [load]);

  const retry = async (e) => {
    setBusy(e.id);
    try { const r = await integrationService.retryError(e.id, params); toast[r.status === "resolved" ? "success" : "error"](`Retry ${r.status}`); load(); }
    catch (err) { toast.error(apiErrorMessage(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  return (
    <div data-testid="error-logs-page">
      <PageHeader icon={TriangleAlert} title="Error Log" subtitle="Failed records are isolated here — canonical data is never corrupted. Retry with backoff.">
        <TenantPicker {...scope} />
      </PageHeader>

      {blocked ? <SelectTenantNotice /> : (
        <>
          <div className="flex bg-muted/40 rounded-lg p-1 mb-4 w-fit" data-testid="error-status-tabs">
            {STATUSES.map((s) => (
              <button key={s} onClick={() => setStatus(s)} className={`px-3 py-1.5 rounded-md text-xs font-semibold capitalize transition-colors ${status === s ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`} data-testid={`error-tab-${s}`}>{s}</button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : items.length === 0 ? (
            <div className="bg-card border border-border rounded-xl p-12 text-center" data-testid="error-empty"><p className="text-sm text-muted-foreground">No {status} errors.</p></div>
          ) : (
            <div className="space-y-2" data-testid="error-list">
              {items.map((e) => (
                <div key={e.id} className="bg-card border border-border rounded-xl p-4 flex items-start gap-3" data-testid={`error-row-${e.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-semibold text-foreground capitalize">{e.stage} error</span>
                      <span className="text-[11px] text-muted-foreground">→ {e.targetEntity}</span>
                      <Badge label={e.status} styleMap={ERROR_STYLE} />
                      <span className="text-[11px] text-muted-foreground">attempt {e.attempts}/{e.maxAttempts}</span>
                    </div>
                    <p className="text-[11px] text-red-600 mt-1">{(e.errors || []).join("; ")}</p>
                    <p className="text-[11px] text-muted-foreground mt-1 bg-muted/40 rounded p-1.5 truncate">{JSON.stringify(e.rawRecord)}</p>
                    <p className="text-[10px] text-muted-foreground/70 mt-1">{fmtDate(e.createdOn)}</p>
                  </div>
                  {e.status === "pending" && (
                    <button onClick={() => retry(e)} disabled={busy === e.id} className="inline-flex items-center gap-1.5 border border-border rounded-lg px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted disabled:opacity-50" data-testid={`error-retry-${e.id}`}>
                      {busy === e.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />} Retry
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
